import { useState } from 'react'
import { Layers, X, GripVertical, ChevronRight } from 'lucide-react'
import { useAppEvent } from '../../AppContext.tsx'

/** Move `drag` to just before `target` in `keys`, returning the new order. */
function reorder(keys: string[], drag: string, target: string): string[] {
  if (drag === target) return keys
  const next = keys.filter((k) => k !== drag)
  const at = next.indexOf(target)
  if (at < 0) return keys
  next.splice(at, 0, drag)
  return next
}

/**
 * Sidebar panel listing the currently-loaded grammar MODULES (see
 * AppState.loadedModules / mergeGrammars). Grammar loading is additive: the
 * workbench composes the rules of every ENABLED module over the PRIMARY
 * module's start axiom. This panel lets the user:
 *
 *  - SEE every loaded module (with the primary one badged),
 *  - toggle each on/off without losing it (a disabled module stays listed but
 *    its rules drop out of the active grammar; re-enabling restores them , no
 *    re-import needed), and
 *  - delete a module entirely (×).
 *
 * It re-renders on the `modules` event so it stays in sync with adds/removes
 * from the Examples menu, the File ▸ Add grammars… import, undo/redo, etc.
 */
export function ModulesSidebar() {
  const app = useAppEvent('modules')
  const modules = app.loadedModules
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)

  const drop = (target: string) => {
    if (dragKey && dragKey !== target) {
      app.reorderModules(
        reorder(
          modules.map((m) => m.key),
          dragKey,
          target,
        ),
      )
    }
    setDragKey(null)
    setOverKey(null)
  }

  return (
    <section className={'panel-section modules-sidebar' + (collapsed ? ' collapsed' : '')}>
      <h3
        className="modules-head"
        role="button"
        aria-expanded={!collapsed}
        title={collapsed ? 'Expand modules' : 'Collapse modules'}
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronRight size={13} className="collapsible-caret" />
        <Layers size={13} /> Modules
        {modules.length > 0 && <span className="group-count">{modules.length}</span>}
      </h3>
      {!collapsed &&
        (modules.length === 0 ? (
          <p className="section-hint">
            No grammar modules loaded. Add some from <strong>File ▸ Add grammars…</strong> or the{' '}
            <strong>Examples</strong> menu , their rules compose together.
          </p>
        ) : (
          <>
            <p className="section-hint">
              Toggle a module to include or exclude its rules. Drag to reorder , the first enabled module supplies the
              start graph.
            </p>
            <div className="module-list">
              {modules.map((m) => (
                <div
                  key={m.key}
                  className={
                    'module-row' +
                    (m.enabled ? '' : ' disabled') +
                    (dragKey === m.key ? ' dragging' : '') +
                    (overKey === m.key ? ' drag-over' : '')
                  }
                  draggable
                  onDragStart={(e) => {
                    setDragKey(m.key)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    if (overKey !== m.key) setOverKey(m.key)
                  }}
                  onDragLeave={() => setOverKey((k) => (k === m.key ? null : k))}
                  onDrop={(e) => {
                    e.preventDefault()
                    drop(m.key)
                  }}
                  onDragEnd={() => {
                    setDragKey(null)
                    setOverKey(null)
                  }}
                >
                  <span className="drag-handle" title="Drag to reorder" aria-hidden>
                    <GripVertical size={14} />
                  </span>
                  <input
                    type="checkbox"
                    className="module-enable"
                    checked={m.enabled}
                    title={m.enabled ? 'Disable , exclude this module’s rules' : 'Enable , include this module’s rules'}
                    onChange={() => app.setModuleEnabled(m.key, !m.enabled)}
                  />
                  <span className="module-title" title={m.title}>
                    {m.title}
                  </span>
                  {m.primary && (
                    <span className="module-primary" title="Supplies the start graph (axiom)">
                      start
                    </span>
                  )}
                  <button
                    className="icon-btn module-del"
                    title={`Remove “${m.title}”`}
                    onClick={() => app.removeGrammarModule(m.key)}
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          </>
        ))}
    </section>
  )
}
